import { Controller } from '@hotwired/stimulus';
import SchoolSearchField from './school_search_field';

const KIDS_MIN_VALUE = 1;
const KIDS_MAX_VALUE = 5;
const KID_FIELDS = ['first_name', 'birth_date', 'current_school_id', 'school_id'];

export default class extends Controller {
  static targets = ['kidsNameForm', 'kidsBirthdateForm', 'kidsSchoolsForm', 'kidSchoolPrompt', 'gradesSelectTemplate', 'formsContainer', 'loadingScreen', 'backArrow'];
  static values = { userId: String, currentStep: { type: Number, default: 0 } };
  steps = ['kidsNameForm', 'kidsBirthdateForm', 'kidsSchoolsForm'];

  kidsInfo = [];
  schools = [];
  schoolSearchableInstances = [];

  async connect() {
    await this.getschoolsData();

    // Warning user about their changes when attempting to refresh page
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) return;

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  beforeUnloadHandler = (event) => {
    event.preventDefault();
    event.returnValue = 'Data will be lost if you leave the page, are you sure?';
  };

  #initializeKidsInfoArray(kidsNum) {
    if (this.kidsInfo.length === kidsNum) return; // Dont reset kids info if filled partially before
    this.kidsInfo = Array.from({ length: kidsNum }, () => ({
      first_name: '',
      birth_date: '',
      current_school_id: '',
      school_id: '',
    }));
  }

  // 🟡 SCENARIO: User doesn't have kids associated to its account 🟡

  // 1️⃣ FIRST STEP FORM - ONBOARDING - fill kids number and names
  handleKidsNumberInput ({ target }) {
    const kidsVal = target.value;
    const kidsNum = isNaN(parseInt(kidsVal)) ? 0 : parseInt(kidsVal);
    const kidsNumValidated = kidsNum >= KIDS_MIN_VALUE && kidsNum <= KIDS_MAX_VALUE ? kidsNum : 0;

    this.#initializeKidsInfoArray(kidsNumValidated);
    this.#setUpKidsNameInputs(kidsNumValidated);

    this.#toggleSubmitButton(this.kidsNameFormTarget);
  }

  handleInputChange({ target }) {
    const index = Number(target.dataset.index);
    const field = target.name;

    const indexIsValid = !isNaN(index) && this.kidsInfo[index];
    const fieldIsValid = KID_FIELDS.includes(field);
  
    if (indexIsValid && fieldIsValid) {
      this.kidsInfo[index][field] = target.value.trim();
    }

    const currentForm = target.closest('form');
    if (currentForm) this.#toggleSubmitButton(currentForm);
  }

  #toggleSubmitButton(form) {
    const submitBtn = form.querySelector('input[type="submit"]');
    if (!submitBtn) return;
  
    let allFilled = false;

    switch (form.name) {
      case 'kidsNameForm':
        allFilled = this.kidsInfo.every(kid => kid.first_name.trim().length > 0);
        break;
      case 'kidsBirthdateForm':
        allFilled = this.kidsInfo.every(kid => kid.birth_date.trim().length > 0);
        break;
      case 'kidsSchoolsForm':
        allFilled = this.kidsInfo.every(kid => kid.school_id && kid.current_school_id);
        break;
    }
  
    submitBtn.disabled = !allFilled;
  }

  /**
   * @param {number} kidsNum number of kids entered
   * It renders the second part of the form, a set of inputs to fill in kids' names
   */
  #setUpKidsNameInputs(kidsNum) {
    const kidsNameContainer = this.kidsNameFormTarget.querySelector('[data-dynamic-container="true"]')
    kidsNameContainer.innerHTML = '';

    if (kidsNum <= 0) return;

    const childCountWord = this.kidsNameFormTarget.querySelector('#child-count');
    if (childCountWord) {
      childCountWord.textContent = kidsNum === 1 ? 'kid' : 'kids';
    }

    const fragment = document.createDocumentFragment();
    const label = document.createElement('span');
    label.className = 'whitespace-nowrap';
    label.textContent = 'named';
    fragment.appendChild(label);

    const kidsInputs = [];

    for (let i = 0; i < kidsNum; i++) {
      kidsInputs.push(this.#buildKidsNameInput(i));
    }

    this.#appendFormattedInputs(fragment, kidsInputs);

    kidsNameContainer.appendChild(fragment);
  }

  /**
   * @param {number} index the kid position within the kidsInfo array
   * @returns an input element to fill in the kid first_name at position [index]
   */
  #buildKidsNameInput(index) {
    const input = document.createElement('input');
    input.name = 'first_name';
    input.type = 'text';
    input.autocomplete= 'off';
    input.className = 'form__input-basic max-w-40 text-24 p-1 font-bold text-center';
    input.dataset.index = index;
    input.dataset.action = 'input->squad-planner-onboarding#handleInputChange';

    return input;
  }

  /**
   * @param {*} fragment the container where the inputs should be appended to
   * @param {*} inputs the kids' names inputs to be appended in the fragment with the right formatting.
   */
  #appendFormattedInputs(fragment, inputs) {
    inputs.forEach((input, index) => {
      fragment.appendChild(input);

      const separator = this.#separator(index, this.kidsInfo.length);
      if (separator) {
        fragment.appendChild(document.createTextNode(separator));
      }
    });
  }

  /**
  * Prepares next form within the onboarding steps
  */
  handleKidsNameFormSubmission(e) {
    e.preventDefault();
    this.kidsNameFormTarget.classList.add('hidden');
    this.#buildKidsBirthdateForm();
    this.currentStepValue++;
  }

  /** Display back arrow from step 1 and onwards */
  currentStepValueChanged() {
    if (!this.hasBackArrowTarget) return;
    if (this.currentStepValue <= 0) this.backArrowTarget.classList.add('hidden')
    else this.backArrowTarget.classList.remove('hidden');
  }

  // 2️⃣ SECOND STEP FORM - ONBOARDING - fill in each kid' birth_date

  /**
   * It builds the form of the kids' birth_date
   */
  #buildKidsBirthdateForm() {
    if (this.kidsInfo.length === 0) return;

    this.kidsBirthdateFormTarget.classList.remove('hidden');
    this.kidsInfo.forEach(kid => { if (!kid.birth_date) { kid.birth_date = ''; } });

    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() - 1);
    const formattedMaxDate = maxDate.toISOString().split('T')[0];

    const kidsBirthdatesFields = this.kidsInfo.map((kid, index) => (
      `
        <div class="flex">
          <div class="flex items-end gap-x-4 gap-y-6 flex-wrap justify-center">
            <p class="inline-block"><span class="text-blue-400">${kid.first_name}</span> was born on</p>
            <input
              name="birth_date"
              data-index=${index}
              data-controller="easepick"
              data-easepick-plugins='["AmpPlugin", "LockPlugin"]'
              type="text"
              value=${kid.birth_date}
              autocomplete="off"
              placeholder="Month - Day - Year"
              name="birth_date"
              data-easepick-amp-dropdown='{ "months": true, "years": true }'
              data-easepick-readonly="false"
              data-easepick-lock-max-date="${formattedMaxDate}"
              class="form__input-basic text-24 placeholder:text-24 placeholder:text-blue-400 text-center max-w-64 md:max-w-72 p-0"
              data-action="easepick:onSelect->squad-planner-onboarding#handleInputChange"
            />
            ${this.#separator(index, this.kidsInfo.length)}
          </div>
        </div>
      `
      )).join(' ');

    this.kidsBirthdateFormTarget.querySelector('[data-dynamic-container="true"]').innerHTML = `
      ${kidsBirthdatesFields}
    `;

    this.#toggleSubmitButton(this.kidsBirthdateFormTarget);
  }

  /**
   * @param {date} birthdate The selected date by the user
   * @returns The kid's age calculation
   */
  #calculateAge(birthdate) {
    const today = new Date();
    let age = today.getFullYear() - birthdate.getFullYear();
    const month = today.getMonth();
    if (month < birthdate.getMonth() || (month === birthdate.getMonth() && today.getDate() < birthdate.getDate())) {
      age--;
    }
    return age > 0 ? age : 0;
  }

  /**
  * Prepares next form within the onboarding steps
  */
  handleKidsBirthDateFormSubmission(e) {
    e.preventDefault();

    this.kidsBirthdateFormTarget.classList.add('hidden');
    this.#buildKidsSchoolForm();
    this.currentStepValue++;
  }

  // 3️⃣ THIRD STEP FORM - ONBOARDING - fill in each kids schools
  #buildKidsSchoolForm() {
    this.kidsSchoolsFormTarget.classList.remove('hidden');
    const kidsFieldsContainer = this.kidsSchoolsFormTarget.querySelector('[data-dynamic-container="true"]');
    kidsFieldsContainer.innerHTML = '';

    const requiresSchoolPrompt = this.kidsInfo.length > 1;

    if (requiresSchoolPrompt) {
      this.kidSchoolPromptTarget.classList.remove('hidden');
      // If option previosly selected, then render correponding school form
      const selectedAnswered = document.querySelector('.school-prompt-btn.btn-blue-active');
      if (selectedAnswered) {
        const kidsBulkUpdate = selectedAnswered.dataset.value === 'yes';
        this.#renderSelectedShoolForm(kidsBulkUpdate);
      }
    } else {
      this.kidSchoolPromptTarget.classList.add('hidden');
      this.#setUpKidsSchoolField();
    }
  }

  handleSchoolPrompt(e) {
    const currentButton = e.srcElement;
    const activeClass = 'btn-blue-active';

    const activeSiblingButton = currentButton.parentElement.querySelector('.btn-blue-active');
    if (activeSiblingButton) activeSiblingButton.classList.remove('btn-blue-active');


    currentButton.classList.add(activeClass);
    const kidsBulkUpdate = currentButton.dataset.value === 'yes';
  
    this.#renderSelectedShoolForm(kidsBulkUpdate);
  }

  #renderSelectedShoolForm(kidsBulkUpdate) {
    if (kidsBulkUpdate) {
      this.#setUpKidsCurrentSchoolBulkField();
    } else {
      this.#setUpKidsSchoolField();
    }

    this.#toggleSubmitButton(this.kidsSchoolsFormTarget);
  }

  /**
   * It builds the field needed to manage current_school_id bulk update
   */
  #setUpKidsCurrentSchoolBulkField() {
    const kidsFieldsContainer = this.kidsSchoolsFormTarget.querySelector('[data-dynamic-container="true"]');
    kidsFieldsContainer.innerHTML = '';

    this.kidsInfo.forEach(kid => {
      if (!kid.school_id) kid.school_id = '';
      if (!kid.current_school_id) kid.current_school_id = '';
    });

    const kidsCurrentSchoolBulkField = `
      <div class="flex flex-col gap-4 items-center">
        <div class="flex gap-4 flex-wrap items-end justify-center" data-school="wrapper">
          <p>They currently go to</p>
        </div>
        <p class="text-18 text-blue-200 h-7 py-2 invisible" id="schoolMessage">Great school!</p>
      </div>

      <button id="bulkSchoolBtn" type="button" class="btn btn-primary btn-hg shrink-0" data-action="click->squad-planner-onboarding#handleBulkCurrentSchoolSubmission" disabled>Continue</button>
    `;

    const allFilledWithSameSchool = this.kidsInfo[0].current_school_id ? this.kidsInfo.every(kid => kid.current_school_id == this.kidsInfo[0].current_school_id) : false;

    kidsFieldsContainer.innerHTML = kidsCurrentSchoolBulkField;
    const schoolFieldWrapper = kidsFieldsContainer.querySelector('[data-school="wrapper"]')
    schoolFieldWrapper.appendChild(this.#buildSchoolsSelect({
      name :'current_school_id',
      onChange: this.handleBulkCurrentSchoolChange,
      value: allFilledWithSameSchool ? this.kidsInfo[0].current_school_id : '',
    }))
    
    const submitBtn = this.kidsSchoolsFormTarget.querySelector('input[type="submit"]')
    if (!submitBtn.classList.contains('hidden')) {
      submitBtn.classList.add('hidden');
    }
  }

  handleBulkCurrentSchoolChange({ target }) {
    const value = target.value;

    this.kidsInfo.forEach(kid => { kid.current_school_id = value; });

    const schoolMessage = this.kidsSchoolsFormTarget.querySelector('#schoolMessage');
    if (schoolMessage && value) {
      schoolMessage.classList.remove('invisible');
    } else {
      schoolMessage.classList.add('invisible');
    }

    this.#toggleBulkSchoolBtn();
  }

  #toggleBulkSchoolBtn() {
    const bulkSchoolBtn = this.kidsSchoolsFormTarget.querySelector('#bulkSchoolBtn');
    const allFilled = this.kidsInfo.every(kid => kid.current_school_id);

    bulkSchoolBtn.disabled = !allFilled;
  }

  /**
   * It builds the second part of the form once current school has been set for all the kids
   */
  handleBulkCurrentSchoolSubmission() {
    const kidsFieldsContainer = this.kidsSchoolsFormTarget.querySelector('[data-dynamic-container="true"]');
    kidsFieldsContainer.innerHTML = '';
  
    const wrapper = document.createElement('div');
    wrapper.className = 'flex gap-x-4 gap-y-6 flex-wrap items-end justify-center';
  
    const introText = document.createElement('p');
    introText.textContent = "Next fall,";
    wrapper.appendChild(introText);
  
    this.kidsInfo.forEach((kid, index) => {
      const kidContainer = document.createElement('div');
      kidContainer.className = "flex flex-col gap-6 flex-wrap items-center";
  
      const innerWrapper = document.createElement('div');
      innerWrapper.className = "flex flex-row gap-x-4 gap-y-6 flex-wrap items-end justify-center";
  
      const text = document.createElement('p');
      text.innerHTML = `<span class="text-blue-400">${kid.first_name}</span> will go to`;
  
      const schoolSelect = this.#buildSchoolsSelect({ 
        name: 'school_id', 
        onChange: this.handleInputChange, 
        dataIndex: index 
      });
  
      innerWrapper.appendChild(text);
      innerWrapper.appendChild(schoolSelect);
      
      const separator = this.#separator(index, this.kidsInfo.length);
      if (separator) {
        const separatorEl = document.createElement('span');
        separatorEl.textContent = separator;
        innerWrapper.appendChild(separatorEl);
      }
  
      kidContainer.appendChild(innerWrapper);
      wrapper.appendChild(kidContainer);
    });
  
    kidsFieldsContainer.appendChild(wrapper);
  
    const submitBtn = this.kidsSchoolsFormTarget.querySelector('input[type="submit"]');
    submitBtn.classList.remove('hidden');
  }

  #setUpKidsSchoolField() {
    const kidsFieldsContainer = this.kidsSchoolsFormTarget.querySelector('[data-dynamic-container="true"]');
    kidsFieldsContainer.innerHTML = '';
  
    this.kidsInfo.forEach((kid, index) => {
      if (!kid.school_id) kid.school_id = '';
      if (!kid.current_school_id) kid.current_school_id = '';

      const kidContainer = document.createElement('div');
      kidContainer.className = "flex flex-col gap-6 flex-wrap items-center";

      const schoolFieldWrapperClass = 'flex flex-row gap-x-4 gap-y-6 flex-wrap items-end justify-center';
  
      // Current school row
      const currentSchoolWrapper = document.createElement('div');
      currentSchoolWrapper.className = schoolFieldWrapperClass;
  
      const currentSchoolText = document.createElement('p');
      currentSchoolText.innerHTML = `<span class="text-blue-400">${kid.first_name}</span> goes to`;
  
      const currentSchoolSelect = this.#buildSchoolsSelect({
        value: kid.current_school_id,
        name: 'current_school_id', 
        onChange: this.handleInputChange, 
        dataIndex: index, 
        placeholder: 'this year' 
      });
  
      currentSchoolWrapper.appendChild(currentSchoolText);
      currentSchoolWrapper.appendChild(currentSchoolSelect);
  
      // Future school row
      const futureSchoolWrapper = document.createElement('div');
      futureSchoolWrapper.className = schoolFieldWrapperClass;
  
      const futureSchoolText = document.createElement('p');
      futureSchoolText.textContent = 'and will attend';
  
      const futureSchoolSelect = this.#buildSchoolsSelect({ 
        value: kid.school_id,
        name: 'school_id', 
        onChange: this.handleInputChange, 
        dataIndex: index, 
        placeholder: 'next year' 
      });
  
      futureSchoolWrapper.appendChild(futureSchoolText);
      futureSchoolWrapper.appendChild(futureSchoolSelect);
  
      kidContainer.appendChild(currentSchoolWrapper);
      kidContainer.appendChild(futureSchoolWrapper);
      
      kidsFieldsContainer.appendChild(kidContainer);
    });

    const submitBtn = this.kidsSchoolsFormTarget.querySelector('input[type="submit"]')
    submitBtn.classList.remove('hidden');
    this.#toggleSubmitButton(this.kidsSchoolsFormTarget);
  }

  handleKidsSchoolFormSubmission(e) {
    e.preventDefault();

    this.kidsSchoolsFormTarget.classList.add('hidden');
    this.formsContainerTarget.classList.add('hidden');
    this.loadingScreenTarget.classList.remove('hidden');

    this.#saveVisitors();
  }

  #buildSchoolsSelect({ value, name, onChange, dataIndex, placeholder = '' }) {
    const selectElement = document.createElement('select');

    selectElement.setAttribute('name', name);
    selectElement.placeholder = placeholder;
    if (dataIndex !== undefined && dataIndex !== null) { selectElement.setAttribute('data-index', dataIndex); }
    if (onChange) { selectElement.addEventListener('change', onChange.bind(this)); }
  
    const selectWrapper = document.createElement('div');
    selectWrapper.className = 'onboarding-school-field'
    selectWrapper.appendChild(selectElement);

    const schoolSearchableField = new SchoolSearchField(selectElement, this.schools, value);
    schoolSearchableField.connect();
    this.schoolSearchableInstances.push(schoolSearchableField);

    return selectWrapper;
  }

  #buildGradesSelect(name, dataAction, dataIndex) {
    const template = this.gradesSelectTemplateTarget;
    const selectElement = template.content.cloneNode(true).querySelector('select');
    
    selectElement.setAttribute('name', name);
    selectElement.setAttribute('data-action', dataAction);
    if (dataIndex !== undefined && dataIndex !== null) { selectElement.setAttribute('data-index', dataIndex); };

    return selectElement;
  }

  async #saveVisitors() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
    try {
      const response = await fetch('/user/visitors/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ visitors: this.kidsInfo })
      });
  
      const data = await response.json();
  
      if (data.success) {
        Turbo.visit(`/users/${this.userIdValue}/summer-squad-planner?onboarding=completed`);
      } else {
        const messages = data?.errors?.join(', ');
        Turbo.visit(`/users/${this.userIdValue}/summer-squad-planner/error?message=${messages}`);
      }
    } catch (error) {
      Turbo.visit(`/users/${this.userIdValue}/summer-squad-planner/error?message=${error?.message || ''}`);
    }
  }

  #separator(index, total) {
    if (index === total - 1) return '';
    if (index === total - 2) return ' and '; // Second-to-last item: 'and'
    return ', '; // Default separator
  }

  async getschoolsData() {
    const csrfToken = document.querySelector("meta[name='csrf-token']").getAttribute('content');
    try {
      const response = await fetch('/schools', {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        }
      });
      const data = await response.json();
      this.schools = data || [];
    } catch (error) {}
  }

  #cleanSchoolsSelect() {
    this.schoolSearchableInstances.forEach(e => e.disconnect());
    this.schoolSearchableInstances = [];
  }

  disconnect() {
    this.#cleanSchoolsSelect();
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // Multi-step form status
  handleStepBack() {
    if (this.currentStepValue <= 0) return;
    this.currentStepValue--;

    const step = this.steps[this.currentStepValue];
    this.#displayStep(step);
  }

  #displayStep(stepName) {
    this.steps.forEach(step => {
      const stepTarget = this[`${step}Target`];
      if (step === stepName) {
        stepTarget.classList.remove('hidden');
      } else {
        stepTarget.classList.add('hidden');
      }
    })
  }
 }
